/**
 * Orders design tokens — the one place the Orders page, its cards and its
 * detail modals take colour, type, radius, shadow and motion from.
 *
 * Every colour is a CSS custom property (`opsCssVars`, set on the Orders page
 * root) read back as `var(--ops-*, <hex>)`. The hex fallback matters: Order.jsx
 * also opens inside the Support Tickets modals, where the Orders page root is
 * not mounted, and it must look the same there.
 *
 * Contrast: text needs 4.5:1. `muted` (#667085) is the lightest colour any text
 * may use; `faint` is for icons and hairlines only. Status chip text uses each
 * tone's `fg`, one step darker than its dot, so it clears 4.5:1 on its own tint.
 */
import { keyframes } from '@emotion/react'

const PALETTE = {
  page: '#F7F8FA',
  surface: '#FFFFFF',
  subtle: '#F9FAFB',
  sunken: '#F2F4F7',
  border: '#EAECF0',
  borderStrong: '#D0D5DD',
  text: '#101828',
  text2: '#344054',
  muted: '#667085',
  faint: '#98A2B3',
  ink: '#101828',
  inkHover: '#1D2939',
  brand: '#ED1D26',
  brandHover: '#C8101A',
  focus: '#1570EF'
}

const kebab = s => s.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)

/** Set on the page root: `<Box sx={opsCssVars}>`. */
export const opsCssVars = Object.fromEntries(
  Object.entries(PALETTE).map(([k, hex]) => [`--ops-${kebab(k)}`, hex])
)

/** Colour references for sx: `ops.border` → `var(--ops-border, #EAECF0)`. */
export const ops = Object.fromEntries(
  Object.entries(PALETTE).map(([k, hex]) => [k, `var(--ops-${kebab(k)}, ${hex})`])
)

/* Type scale, px: 12 / 13 / 14 / 16 / 20 / 28, plus 11 for uppercase labels. */
export const FS = { label: 11, xs: 12, sm: 13, md: 14, lg: 16, xl: 20, xxl: 28 }

export const RADIUS = { card: '14px', tile: '12px', button: '10px', chip: '999px', modal: '20px' }

export const SHADOW = {
  card: '0 1px 2px rgba(16,24,40,.04), 0 4px 12px rgba(16,24,40,.06)',
  cardHover: '0 2px 4px rgba(16,24,40,.06), 0 12px 24px rgba(16,24,40,.10)',
  pop: '0 1px 2px rgba(16,24,40,.05)',
  modal: '0 24px 48px -12px rgba(16,24,40,.28)'
}

export const EASE = 'cubic-bezier(0.2, 0, 0, 1)'

/** Prices, times, phone numbers and order ids line up in columns. */
export const tabular = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' }

/** Small uppercase section label. */
export const labelSx = {
  fontSize: FS.label,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: ops.muted,
  lineHeight: 1.4
}

export const focusRing = {
  '&:focus-visible': { outline: `2px solid ${ops.focus}`, outlineOffset: 2 }
}

export const noMotion = {
  '@media (prefers-reduced-motion: reduce)': { animation: 'none !important', transition: 'none !important' }
}

/* ── Status tones ─────────────────────────────────────────────────────────
   One semantic map, used by chips, card accents, filter pills, KPI tiles and
   the modals. `fg` is chip text, `bg` the ~10% tint behind it, `dot` the mark. */
export const TONES = {
  slate: { fg: '#475467', bg: 'rgba(102,112,133,0.12)', dot: '#667085' },
  amber: { fg: '#B54708', bg: 'rgba(247,144,9,0.12)', dot: '#F79009' },
  blue: { fg: '#175CD3', bg: 'rgba(46,144,250,0.12)', dot: '#2E90FA' },
  violet: { fg: '#6927DA', bg: 'rgba(122,90,248,0.12)', dot: '#7A5AF8' },
  sky: { fg: '#026AA2', bg: 'rgba(11,165,236,0.12)', dot: '#0BA5EC' },
  emerald: { fg: '#067647', bg: 'rgba(23,178,106,0.12)', dot: '#17B26A' },
  rose: { fg: '#C01048', bg: 'rgba(246,61,104,0.12)', dot: '#F63D68' },
  refunded: { fg: '#5B5876', bg: 'rgba(110,101,145,0.14)', dot: '#8B85A8' },
  neutral: { fg: '#344054', bg: '#F2F4F7', dot: '#98A2B3' }
}

export const tone = name => TONES[name] || TONES.neutral

/** Marketplace orderStatus (and the page's REFUNDED tab) → tone. */
export const ORDER_TONE = {
  SCHEDULED: 'slate',
  PENDING: 'amber',
  ACCEPTED: 'blue',
  PREPARED: 'violet',
  ASSIGNED: 'sky',
  PICKED: 'sky',
  DELIVERED: 'emerald',
  CANCELLED: 'rose',
  REFUNDED: 'refunded'
}

/* ── Motion ─────────────────────────────────────────────────────────────── */
export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`
export const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(23,178,106,0.55); }
  70%  { box-shadow: 0 0 0 6px rgba(23,178,106,0); }
  100% { box-shadow: 0 0 0 0 rgba(23,178,106,0); }
`
export const shimmer = keyframes`
  from { background-position: -320px 0; }
  to   { background-position: 320px 0; }
`
export const arrivalGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(247,144,9,0), 0 1px 2px rgba(16,24,40,.04); }
  15%  { box-shadow: 0 0 0 4px rgba(247,144,9,0.28), 0 8px 24px rgba(247,144,9,0.18); }
  100% { box-shadow: 0 0 0 0 rgba(247,144,9,0), 0 1px 2px rgba(16,24,40,.04); }
`
export const modalIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(4px); }
  to   { opacity: 1; transform: none; }
`

/** Paper styling shared by every Orders dialog. */
export const dialogPaperSx = {
  borderRadius: RADIUS.modal,
  boxShadow: SHADOW.modal,
  bgcolor: ops.surface,
  animation: `${modalIn} 180ms ${EASE}`,
  ...noMotion
}

export const backdropSx = {
  backgroundColor: 'rgba(16,24,40,0.45)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)'
}

/** Buttons. Height 36, radius 10, no uppercase. */
const buttonBase = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: FS.sm,
  borderRadius: RADIUS.button,
  minHeight: 36,
  boxShadow: 'none',
  transition: `background-color 150ms ${EASE}, border-color 150ms ${EASE}, color 150ms ${EASE}`,
  '&:hover': { boxShadow: 'none' },
  ...focusRing
}

export const btn = {
  /* Solid, in a status tone: the next step for an order (Accept, Mark Ready…). */
  solid: (t, hover) => ({
    ...buttonBase,
    bgcolor: t,
    color: '#fff',
    '&:hover': { bgcolor: hover || t, boxShadow: 'none' }
  }),
  primary: {
    ...buttonBase,
    bgcolor: ops.brand,
    color: '#fff',
    '&:hover': { bgcolor: ops.brandHover, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: ops.sunken, color: ops.faint }
  },
  secondary: {
    ...buttonBase,
    bgcolor: ops.surface,
    color: ops.text2,
    border: `1px solid ${ops.borderStrong}`,
    '&:hover': { bgcolor: ops.subtle, borderColor: ops.borderStrong }
  },
  danger: {
    ...buttonBase,
    bgcolor: ops.surface,
    color: TONES.rose.fg,
    border: `1px solid ${ops.border}`,
    '&:hover': { bgcolor: TONES.rose.bg, borderColor: 'rgba(246,61,104,0.35)' }
  }
}
